<template>
	<div class="small-player-shell flex flex-col my-1 p-2 sm:p-1 rounded-xl shadow-sm border relative w-full max-w-full sm:max-w-[420px]">
		<EditButton
			v-if="props.collectionMode?.editmode"
			class="absolute right-[5px] top-[10px]"
			:id="jsonData.id"
			type="podcast"
		/>
		<!-- <div class="flex items-center flex-1 mb-2">
			<div>
				<p class="text-primary font-semibold">{{ jsonData.mainOutcome.selectHeader}}</p>
				<h2 class="text-lg font-600 md:text-sm">{{ jsonData.mainOutcome.title }}</h2>
			</div>
		</div> -->
		<div class="audio-player small-player-layout flex flex-col sm:flex-row items-stretch sm:items-center w-full gap-2">
			<div class="small-player-main-row flex items-center w-full gap-2">
				<button
					@click="togglePlayback"
					class="small-player-play-btn hover:bg-success hover:text-white bg-[#4d6680] text-white font-bold w-[44px] h-[44px] rounded-full flex justify-center items-center border border-[#3f5873]"
				>
					<EwIcon
						:icon="isPlaying ? 'tabler:player-pause' : 'tabler:player-play'"
						class="cursor-pointer w-[16px] h-[16px] sm:w-[15px] sm:h-[15px] md:text-base"
					/>
				</button>
				<div v-if="isLoading" class="soundPlayer-spinner">
					<div class="soundPlayer-spinner-border"></div>
				</div>

				<div class="small-player-slider-wrap flex-1 min-w-0">
					<input
						type="range"
						min="0"
						:max="duration"
						v-model="currentTime"
						@input="seek"
						class="w-full appearance-none cursor-pointer h-1 rounded-full bg-gray-200 focus:outline-none soundPlayer-slider"
					/>
				</div>
			</div>
			<div class="small-player-time-row flex items-center justify-between px-[2px]">
				<p class="text-gray-500 font-semibold text-xs">{{ formatTime(currentTime) }}</p>
				<p class="text-gray-500 font-semibold text-xs">{{ formatTime(duration) }}</p>
			</div>

			<div class="hidden sm:flex items-center self-end sm:self-center">
				<button
					@click="toggleMute"
					class="mr-2 hover:text-success-hover text-[#4d6680] font-bold w-[28px] h-[28px] rounded-full flex justify-center items-center"
				>
					<EwIcon :icon="isMuted ? 'tabler:volume-3' : 'tabler:volume-2'" class="cursor-pointer md:text-lg" />
				</button>
				<input
					type="range"
					min="0"
					max="100"
					v-model="volume"
					@input="setVolume"
					class="w-20 appearance-none cursor-pointer h-1 rounded-full bg-gray-200 focus:outline-none soundPlayer-slider"
				/>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { ref, watch, onUnmounted, defineProps } from 'vue';
import { onBeforeRouteLeave } from 'vue-router';
import EditButton from '../components/EditButton.vue';

import { Howl } from 'howler';
import type { configCollectionMode } from '@/types';

const props = defineProps<{

	modalVisibleAudio: boolean;
	jsonData: {
		id: string;
		mainOutcome:{
			title: string;
			selectHeader: string;
		}
		file_url: string;
	};
	collectionMode?: configCollectionMode | null;
}>();

const { jsonData } = props;

const isPlaying = ref(false);
const volume = ref(50);
const currentTime = ref(0);
const duration = ref(0);
const isMuted = ref(false);
const isLoading = ref(false);

let sound: Howl | null = null;

const createSound = () => {
	sound = new Howl({
		src: [jsonData.file_url],
		preload: true,

		volume: volume.value / 100,
		onplay: () => {
			isPlaying.value = true;
			isLoading.value = false; // Muzika ka filluar të luajë, ndalojmë ngarkimin
			updateCurrentTime();
		},
		onpause: () => {
			isPlaying.value = false;
		},
		onend: () => {
			isPlaying.value = false;
		},
		onseek: () => {
			currentTime.value = sound!.seek();
		},
		onload: () => {
			if (isLoading.value) {
				isLoading.value = false; // Ndalojmë ngarkimin kur muzika është gati
				sound!.play(); // Fillojmë të luajmë muzikën pas ngarkimit
			}
			duration.value = sound!.duration(); // Vendosim kohëzgjatjen e audios
		},
		onloaderror: (error: any) => {
			console.error('Error loading sound:', error);
		},
		onplayerror: (error: any) => {
			console.error('Error playing sound:', error);
		},
	});
};

const togglePlayback = () => {
	if (!sound) {
		isLoading.value = true;
		createSound();
	} else {
		isPlaying.value ? sound!.pause() : sound!.play();
	}
};
const seek = () => {
	if (sound) {
		sound.seek(currentTime.value);
	}
};

const setVolume = () => {
	if (sound) {
		sound.volume(isMuted.value ? 0 : volume.value / 100);
	}
};

const toggleMute = () => {
	isMuted.value = !isMuted.value;
	setVolume();
};

const formatTime = (timeInSeconds: number) => {
	const minutes = Math.floor(timeInSeconds / 60);
	const seconds = Math.floor(timeInSeconds % 60);
	return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
};

const updateCurrentTime = () => {
	const interval = setInterval(() => {
		if (isPlaying.value && sound) {
			currentTime.value = sound.seek();
		}
	}, 1000);

	onUnmounted(() => {
		clearInterval(interval);
	});
};

const stopAudio = () => {
	if (sound) {
		sound.stop();
	}
	isPlaying.value = false;
	currentTime.value = 0;
};

watch(
	() => props.modalVisibleAudio,
	(newVal) => {
		if (!newVal) {
			stopAudio();
		}
	}
);
watch(isPlaying, (newVal, oldVal) => {
	if (oldVal === false && newVal === true) {
		console.log('Muzika filloi të luajë!');
	}
});

onBeforeRouteLeave((to, from, next) => {
	stopAudio();
	next();
});
</script>

<style scoped>
.small-player-shell {
	background:
		linear-gradient(180deg, rgba(255, 255, 255, 0.98) 0%, rgba(246, 250, 255, 0.98) 100%);
	border: 1px solid #d6e0ee;
	box-shadow:
		0 10px 24px rgba(16, 24, 40, 0.08),
		0 1px 0 rgba(255, 255, 255, 0.9) inset;
}

.small-player-layout {
	min-width: 0;
}

.small-player-main-row {
	min-width: 0;
	align-items: center;
}

.small-player-play-btn {
	flex-shrink: 0;
	width: 52px !important;
	height: 52px !important;
	box-shadow:
		0 8px 16px rgba(77, 102, 128, 0.28),
		0 1px 0 rgba(255, 255, 255, 0.25) inset;
	transition: transform 0.16s ease, box-shadow 0.16s ease, background-color 0.16s ease;
}

.small-player-play-btn:hover {
	transform: translateY(-1px);
}

.small-player-slider-wrap {
	min-width: 0;
	display: flex;
	align-items: center;
	height: 60px;
}

.small-player-time-row {
	font-variant-numeric: tabular-nums;
	letter-spacing: 0.01em;
	margin-top: -14px;
}

.soundPlayer-slider {
	width: 100%;
	height: 6px;
	background: #d4deea;
	cursor: pointer;
	border-radius: 999px;
	margin-right: 0;
	padding: 0;
	border: none !important;
	accent-color: #35b7a5;
}

.soundPlayer-slider::-webkit-slider-thumb {
	-webkit-appearance: none;
	appearance: none;
	width: 16px;
	height: 16px;
	border-radius: 999px;
	background: #ffffff;
	border: 2px solid #35b7a5;
	box-shadow: 0 0 0 3px rgba(53, 183, 165, 0.2);
}

.soundPlayer-slider::-moz-range-thumb {
	width: 16px;
	height: 16px;
	border-radius: 999px;
	background: #ffffff;
	border: 2px solid #35b7a5;
	box-shadow: 0 0 0 3px rgba(53, 183, 165, 0.2);
}

.soundPlayer-spinner {
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
}

.soundPlayer-spinner-border {
	border: 4px solid rgba(0, 0, 0, 0.1);
	border-left-color: #000;
	border-radius: 50%;
	width: 3rem;
	height: 3rem;
	animation: spin 1s infinite linear;
}

@keyframes spin {
	to {
		transform: rotate(360deg);
	}
}

@media (max-width: 639.98px) {
	.small-player-shell {
		width: calc(100% - 1.25rem);
		max-width: 100%;
		margin-left: auto;
		margin-right: auto;
		padding: 0.58rem 0.62rem;
		border-radius: 14px;
	}

	.small-player-play-btn {
		width: 60px !important;
		height: 60px !important;
	}

	.small-player-main-row {
		gap: 0.7rem;
	}

	.small-player-slider-wrap {
		height: 60px;
	}

	.small-player-time-row {
		margin-top: -16px;
		padding-left: calc(60px + 0.7rem);
	}

	.small-player-time-row p {
		font-size: 0.75rem;
	}
}
</style>
